/**
 * Synthesized sounds (practice QoL, batch 3 #23): the "attention" chime (the turn, a decision, priority or the
 * showdown focus reaching you) and soft "game feedback" clicks for board events (a draw, a play, a score,
 * damage). Everything is a short WebAudio tone, no assets. Volumes come from the `sound` settings: a master
 * level, one level per channel and a mute switch ({@link channelGain}).
 *
 * Browsers start audio suspended until a user gesture (autoplay policy). {@link unlockAudio} resumes the
 * context from a click or key press (the board arms it on the first gesture, the settings page's "Test sound"
 * calls it directly); while the browser refuses, {@link audioBlocked} is true and the settings page says so.
 * Every call fails quietly where audio is unavailable: the title alert still works.
 */
import type { ViewEvent } from '@play/protocol';
import type { PlaySettings } from '../settings/schema';

/** A sound channel with its own volume. */
export type SoundChannel = 'attention' | 'feedback';

/** The sounds there are. */
export type SoundKind = 'chime' | 'draw' | 'play' | 'score' | 'damage';

/** The channel each sound plays on. */
export const SOUND_CHANNEL: Readonly<Record<SoundKind, SoundChannel>> = {
  chime: 'attention',
  draw: 'feedback',
  play: 'feedback',
  score: 'feedback',
  damage: 'feedback',
};

/** The gain (0-1) a channel plays at under these settings: 0 when muted or switched off. */
export function channelGain(sound: PlaySettings['sound'], channel: SoundChannel): number {
  const c = sound[channel];
  if (sound.muted || !c.on) return 0;
  return clamp01(sound.master) * clamp01(c.volume);
}

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/** Order of precedence when one update has several sounds: only the most telling one plays. */
const RANK: readonly SoundKind[] = ['score', 'damage', 'play', 'draw'];

/**
 * The feedback sound for one update's events (at most one, so a busy update stays a single soft click):
 * a score or points gained, damage or a kill, a play, a draw. `null` when nothing is worth a sound.
 */
export function feedbackSoundFor(events: readonly ViewEvent[]): SoundKind | null {
  const found = new Set<SoundKind>();
  for (const e of events) {
    if (e.type === 'scored' || (e.type === 'pointsChanged' && e.delta > 0)) found.add('score');
    else if ((e.type === 'damaged' && e.amount > 0) || e.type === 'killed') found.add('damage');
    else if (e.type === 'played') found.add('play');
    else if (e.type === 'moved' && e.from.kind === 'hand' && (e.to.kind === 'base' || e.to.kind === 'battlefield'))
      found.add('play'); // tier 1: plays are moves from hand
    else if (e.type === 'drew' && e.cards.length > 0) found.add('draw');
  }
  return RANK.find((k) => found.has(k)) ?? null;
}

/** Notes of each sound: frequency (Hz), start offset (s), length (s), peak (0-1 before the channel gain). */
type Note = readonly [freq: number, at: number, len: number, peak: number, type?: OscillatorType];

const NOTES: Readonly<Record<SoundKind, readonly Note[]>> = {
  chime: [
    [660, 0, 0.32, 0.12],
    [880, 0.11, 0.32, 0.12],
  ],
  draw: [[1250, 0, 0.05, 0.05, 'triangle']],
  play: [
    [520, 0, 0.08, 0.06, 'triangle'],
    [780, 0.04, 0.09, 0.05, 'triangle'],
  ],
  score: [
    [740, 0, 0.14, 0.07],
    [988, 0.08, 0.2, 0.07],
  ],
  damage: [[180, 0, 0.12, 0.08, 'triangle']],
};

let context: AudioContext | null = null;
let blocked = false;
const listeners = new Set<() => void>();

function setBlocked(value: boolean): void {
  if (blocked === value) return;
  blocked = value;
  listeners.forEach((l) => {
    l();
  });
}

/** Whether the browser refused to start audio (no user gesture yet, or autoplay blocked). */
export function audioBlocked(): boolean {
  return blocked;
}

/** Subscribes to {@link audioBlocked} changes (for `useSyncExternalStore`). */
export function subscribeAudio(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function ctx(): AudioContext | null {
  try {
    if (context) return context;
    const Ctor = typeof window !== 'undefined' ? window.AudioContext : undefined;
    if (!Ctor) return null;
    context = new Ctor();
    return context;
  } catch {
    return null;
  }
}

/** Resumes a suspended context; marks audio blocked when the browser refuses. Never rejects. */
function resume(c: AudioContext): Promise<boolean> {
  if (c.state === 'running') {
    setBlocked(false);
    return Promise.resolve(true);
  }
  try {
    return c.resume().then(
      () => {
        const ok = c.state === 'running';
        setBlocked(!ok);
        return ok;
      },
      () => {
        setBlocked(true);
        return false;
      },
    );
  } catch {
    setBlocked(true);
    return Promise.resolve(false);
  }
}

/** Starts audio from a user gesture (a click or key press). Resolves `false` when the browser still refuses. */
export function unlockAudio(): Promise<boolean> {
  const c = ctx();
  if (!c) return Promise.resolve(false);
  return resume(c);
}

/** Plays a sound at `gain` (0-1, see {@link channelGain}). Silent at 0, without audio, or while blocked. */
export function playSound(kind: SoundKind, gain: number): void {
  if (!(gain > 0)) return;
  const c = ctx();
  if (!c) return;
  const start = () => {
    try {
      const t0 = c.currentTime + 0.01;
      for (const [freq, at, len, peak, type = 'sine'] of NOTES[kind]) {
        const osc = c.createOscillator();
        const amp = c.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        amp.gain.setValueAtTime(0, t0 + at);
        amp.gain.linearRampToValueAtTime(Math.max(0.0002, peak * gain), t0 + at + 0.012);
        amp.gain.exponentialRampToValueAtTime(0.0001, t0 + at + len);
        osc.connect(amp).connect(c.destination);
        osc.start(t0 + at);
        osc.stop(t0 + at + len + 0.03);
      }
    } catch {
      // A closed or broken context: stay silent.
    }
  };
  if (c.state === 'running') start();
  else
    void resume(c).then((ok) => {
      if (ok) start();
    });
}
